
import { defineComponent, nextTick, reactive, ref, toRefs, watch } from 'vue'
import ImageDisplay from '@/components/ImageDisplay.vue'
import { isFileType } from '@/utils/file'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import { Tools } from '@carbon/charts/tools'
import updateLegendAdditionalItems = Tools.updateLegendAdditionalItems
import { useQuasar } from 'quasar'

export default defineComponent({
  name: 'PictureDrop',
  components: { TrashIcon, ImageDisplay },
  props: {
    logo: {
      type: Object,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    preParse: Function,
    postParse: Function
  },
  emits: ['change', 'remove-logo'],

  setup (props, { emit }) {
    const $q = useQuasar()

    const closeErrorNotification = ref<(() => any) | null>(null)

    const state = reactive({
      loading: false,
      importDone: false,
      upload: ref(),
      fileList: ref([] as any[]),
      fileInput: ref(),
    })

    const uploadedFileName = ref('')

    const accepts = ['.jpg', '.jpeg', '.png', '.svg']

    watch(() => state.fileList, () => {
      emit('change', { files: state.fileList })
    }, { deep: true })

    watch(() => state.upload, () => {
      emit('change', state.upload)
    })

    async function previewImage () {
      await nextTick()
      if (state?.fileList[0]) {
        const el = document.querySelector('.productImage')
        if (el) {
          el?.setAttribute('src', URL.createObjectURL(state.fileList[0]))
        }
      }
    }

    function onChange (event) {
      const files = event.target.files
      const rawFile = files[0] // Only use the first one, because we are accepting only one file
      if (!rawFile) return
      closeErrorNotification.value?.()

      // Input handles checks itself so we can skip those
      assert(event)
    }

    function assert (event) {
      const files: Array<File> = event.type === 'change' ? event.target.files : event.dataTransfer.files
      console.log(files)
      if (files.length > 1) {
        closeErrorNotification.value = $q.notify({
          message: 'Only supports uploading of 1 file!',
          type: 'negative',
          position: 'top',
          timeout: 5000,
        })
        event.target.value = ''
        if (!event.currentTarget.classList.contains('file-drop--error')) {
          event.currentTarget.classList.add('file-drop--error')
        }
        return
      }

      // console.log('Files list: ', files)
      const rawFile = files[0] // Only use the first one, because we are accepting only one file
      if (!isFileType(rawFile, accepts)) {
        closeErrorNotification.value = $q.notify({
          message: `Only supports upload for ${accepts}`,
          type: 'negative',
          position: 'top',
          timeout: 5000,
        })
        event.target.value = ''
        if (!event.currentTarget.classList.contains('file-drop--error')) {
          event.currentTarget.classList.add('file-drop--error')
        }
        return false
      }

      if (rawFile.size > 10485760) {
        closeErrorNotification.value = $q.notify({
          message: 'Only supports upload for files smaller than 10MB',
          type: 'negative',
          position: 'top',
          timeout: 5000,
        })
        event.target.value = ''
        if (!event.currentTarget.classList.contains('file-drop--error')) {
          event.currentTarget.classList.add('file-drop--error')
        }
        return false
      }

      parse(rawFile)
    }

    function remove () {
      state.fileList = []
      state.upload = null
      // state.importDone = false
      uploadedFileName.value = ''
      state.fileInput.value = []

      emit('remove-logo')
    }

    function parse (file: File) {
      state.fileList[0] = file

      if (!props.preParse) {
        parseFile(file)
        return
      }

      if (props.preParse(file)) {
        parseFile(file)
      }
    }

    function parseFile (file: File) {
      state.loading = true

      if (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/svg+xml') {
        const reader = new FileReader()
        state.loading = true

        reader.onload = function () {
          state.upload = { fileName: file.name, file: reader.result as string }
          uploadedFileName.value = file.name
          state.loading = false
          void previewImage()
        }

        reader.onerror = e => {
          state.loading = false
          console.error(e)
        }

        reader.readAsDataURL(file)

        return
      }
    }

    function dragover (event) {
      event.preventDefault()
      if (event.currentTarget.classList.contains('file-drop--error')) event.currentTarget.classList.remove('file-drop--error')
      if (!event.currentTarget.classList.contains('file-drop--hover')) event.currentTarget.classList.add('file-drop--hover')
    }

    function dragleave (event) {
      event.currentTarget.classList.remove('file-drop--hover')
    }

    function drop (event) {
      event.stopPropagation()
      event.preventDefault()

      assert(event)
    }

    return {
      ...toRefs(state),
      onChange,
      remove,
      dragover,
      dragleave,
      drop,
      accepts,
      uploadedFileName
    }
  }
})
